import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { X, Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useNavigate } from 'react-router-dom';

interface Promo {
  id?: string;
  level: string;
  writerName: string;
  producerName: string;
  promosTested: string[];
  bestCpi: number;
  percentMaleInCpi: number;
  bestCpt: number;
  percentMaleInCpt: number;
  promosWithCpiLessThan2: string[];
  promosWithCptLessThan50: string[];
  isOutsourced?: boolean; // Flag to identify outsourced promos
  showName?: string; // Name of the show this promo belongs to
}

interface PromoDetailsData {
  promos: Promo[];
  successfulPromoLinks: string[];
  promoPerformanceDetails: string;
  outsourcedShow?: any;
}

interface PromoDetailsFormProps {
  data: PromoDetailsData;
  onChange: (data: PromoDetailsData) => void;
}

export const PromoDetailsForm: React.FC<PromoDetailsFormProps> = ({ data, onChange }) => {
  // Ensure data has the expected structure with defaults
  const safeData = data || { promos: [], successfulPromoLinks: [], promoPerformanceDetails: '', outsourcedShow: null };
  const navigate = useNavigate();

  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promo | null>(null);
  const [newSuccessfulPromoLink, setNewSuccessfulPromoLink] = useState('');
  const [tempInputs, setTempInputs] = useState({
    promosTested: '',
    promosWithCpiLessThan2: '',
    promosWithCptLessThan50: ''
  });

  const updateData = (updates: Partial<PromoDetailsData>) => {
    onChange({ ...safeData, ...updates });
  };

  const handleEditOutsourcedShow = () => {
    if (safeData.outsourcedShow?.id) {
      // Navigate to outsourced shows page with edit mode for the specific show
      navigate(`/outsourced-shows?edit=${safeData.outsourcedShow.id}`);
    }
  };

  const addPromo = () => {
    const newPromo: Promo = {
      // No id for new promos - backend will assign
      level: '',
      writerName: '',
      producerName: '',
      promosTested: [],
      bestCpi: 0,
      percentMaleInCpi: 0,
      bestCpt: 0,
      percentMaleInCpt: 0,
      promosWithCpiLessThan2: [],
      promosWithCptLessThan50: []
    };
    setEditingPromo(newPromo);
    setIsEditing(true);
  };

  const editPromo = (promo: Promo) => {
    setEditingPromo({ ...promo });
    setIsEditing(true);
  };

  const savePromo = () => {
    if (!editingPromo) return;
    
    const updatedPromos = editingPromo.id && safeData.promos.find(p => p.id === editingPromo.id)
      ? safeData.promos.map(p => p.id === editingPromo.id ? editingPromo : p)
      : [...safeData.promos, editingPromo]; // No client-side ID assignment
    
    updateData({ promos: updatedPromos });
    setSelectedPromo(editingPromo);
    setIsEditing(false);
    setEditingPromo(null);
  };

  const deletePromo = (promoId: string) => {
    const updatedPromos = safeData.promos.filter(p => p.id !== promoId);
    updateData({ promos: updatedPromos });
    if (selectedPromo?.id === promoId) {
      setSelectedPromo(null);
    }
  };

  const addArrayItem = (field: keyof Pick<Promo, 'promosTested' | 'promosWithCpiLessThan2' | 'promosWithCptLessThan50'>, value: string) => {
    if (!editingPromo || !value.trim()) return;
    
    const currentArray = editingPromo[field] || [];
    const newArray = [...currentArray, value.trim()];
    setEditingPromo({ ...editingPromo, [field]: newArray });
    setTempInputs({ ...tempInputs, [field]: '' });
  };

  const removeArrayItem = (field: keyof Pick<Promo, 'promosTested' | 'promosWithCpiLessThan2' | 'promosWithCptLessThan50'>, index: number) => {
    if (!editingPromo) return;
    
    const currentArray = editingPromo[field] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    setEditingPromo({ ...editingPromo, [field]: newArray });
  };

  const handleKeyPress = (field: keyof Pick<Promo, 'promosTested' | 'promosWithCpiLessThan2' | 'promosWithCptLessThan50'>, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addArrayItem(field, tempInputs[field]);
    }
  };

  const addSuccessfulPromoLink = () => {
    if (newSuccessfulPromoLink.trim() && safeData.successfulPromoLinks.length < 5) {
      const newLinks = [...safeData.successfulPromoLinks, newSuccessfulPromoLink.trim()];
      updateData({ successfulPromoLinks: newLinks });
      setNewSuccessfulPromoLink('');
    }
  };

  const removeSuccessfulPromoLink = (index: number) => {
    const newLinks = safeData.successfulPromoLinks.filter((_, i) => i !== index);
    updateData({ successfulPromoLinks: newLinks });
  };

  const handleSuccessfulPromoLinkKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSuccessfulPromoLink();
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Promo Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="promos" className="space-y-4">
            <TabsList>
              <TabsTrigger value="promos">
                Promos ({safeData.promos.length})
                {safeData.promos.some(p => p.isOutsourced) && (
                  <span className="text-xs ml-1 text-blue-600">
                    ({safeData.promos.filter(p => !p.isOutsourced).length} + {safeData.promos.filter(p => p.isOutsourced).length} outsourced)
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="successful">Successful Links ({safeData.successfulPromoLinks.length}/5)</TabsTrigger>
              <TabsTrigger value="details">Promo Details</TabsTrigger>
            </TabsList>

            <TabsContent value="promos" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Promo Management</h3>
                <Button onClick={addPromo} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Promo
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Promos List */}
                <div className="space-y-2">
                  <h4 className="font-medium">Promos</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {safeData.promos.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No promos added yet</p>
                    ) : (
                      safeData.promos.map((promo, index) => (
                        <div
                          key={promo.id || `promo-${index}`}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            promo.isOutsourced
                              ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800/50'
                              : 'bg-background'
                          } ${
                            selectedPromo?.id === promo.id ? 'border-primary' : 'hover:bg-muted/50'
                          }`}
                          onClick={() => setSelectedPromo(promo)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{promo.level || 'Untitled'}</p>
                                {promo.isOutsourced && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium dark:bg-blue-900/50 dark:text-blue-300">
                                    Outsourced
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">Writer: {promo.writerName}</p>
                              {promo.isOutsourced && promo.showName && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">From: {promo.showName}</p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              {promo.isOutsourced ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-blue-600"
                                  title="Edit source show"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditOutsourcedShow();
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      editPromo(promo);
                                    }}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deletePromo(promo.id!);
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Promo Details */}
                <div className="col-span-2">
                  {selectedPromo ? (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{selectedPromo.level}</h4>
                          {selectedPromo.isOutsourced && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium dark:bg-blue-900/50 dark:text-blue-300">
                              Outsourced
                            </span>
                          )}
                        </div>
                        {selectedPromo.isOutsourced ? (
                          <Button
                            variant="outline"
                            onClick={handleEditOutsourcedShow}
                            className="text-blue-600 border-blue-600 hover:bg-blue-50"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Edit Source Show
                          </Button>
                        ) : (
                          <Button variant="outline" onClick={() => editPromo(selectedPromo)}>
                            Edit
                          </Button>
                        )}
                      </div>
                      {selectedPromo.isOutsourced && selectedPromo.showName && (
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          <strong>Source:</strong> {selectedPromo.showName}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <strong>Writer:</strong> {selectedPromo.writerName}
                        </div>
                        <div>
                          <strong>Producer:</strong> {selectedPromo.producerName}
                        </div>
                        <div>
                          <strong>Best CPI:</strong> {selectedPromo.bestCpi} ({selectedPromo.percentMaleInCpi}% male)
                        </div>
                        <div>
                          <strong>Best CPT:</strong> {selectedPromo.bestCpt} ({selectedPromo.percentMaleInCpt}% male)
                        </div>
                      </div>
                      
                      {selectedPromo.promosTested?.length > 0 && (
                        <div>
                          <strong className="text-sm">Promos Tested:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedPromo.promosTested?.map((promo, idx) => (
                              <Badge key={idx} variant="secondary">{promo}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedPromo.promosWithCpiLessThan2?.length > 0 && (
                        <div>
                          <strong className="text-sm">Promos with CPI &lt; 2:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedPromo.promosWithCpiLessThan2?.map((promo, idx) => (
                              <Badge key={idx} variant="default">{promo}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedPromo.promosWithCptLessThan50?.length > 0 && (
                        <div>
                          <strong className="text-sm">Promos with CPT &lt; 50:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedPromo.promosWithCptLessThan50?.map((promo, idx) => (
                              <Badge key={idx} variant="outline">{promo}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-muted-foreground">
                      Select a promo to view details
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="successful" className="space-y-4">
              {/* Top 5 Successful Promo Links */}
              <div>
                <h3 className="text-lg font-medium mb-4">Top 5 Successful Promo Links</h3>
                
                {safeData.successfulPromoLinks.length === 0 && (
                  <p className="text-sm text-muted-foreground mb-4">No successful promo links added yet.</p>
                )}
                
                {safeData.successfulPromoLinks.map((link, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm truncate">{index + 1}. {link}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => removeSuccessfulPromoLink(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                
                {safeData.successfulPromoLinks.length < 5 && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter promo link URL"
                      value={newSuccessfulPromoLink}
                      onChange={(e) => setNewSuccessfulPromoLink(e.target.value)}
                      onKeyDown={handleSuccessfulPromoLinkKeyPress}
                    />
                    <Button
                      onClick={addSuccessfulPromoLink}
                      disabled={!newSuccessfulPromoLink.trim()}
                    >
                      Add Link
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="details" className="space-y-4">
              {/* Promo Performance Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Promo Performance Details</h3>
                <Textarea
                  placeholder="Enter detailed promo performance analysis and insights..."
                  value={safeData.promoPerformanceDetails}
                  onChange={(e) => updateData({ promoPerformanceDetails: e.target.value })}
                  rows={6}
                  className="w-full"
                />
                <p className="text-sm text-muted-foreground">
                  Add comprehensive analysis of promo performance
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit Promo Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPromo?.id && safeData.promos.find(p => p.id === editingPromo.id) ? 'Edit Promo' : 'Add New Promo'}</DialogTitle>
          </DialogHeader>
          {editingPromo && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="level">Level *</Label>
                  <Input
                    id="level"
                    placeholder="Enter level"
                    value={editingPromo.level}
                    onChange={(e) => setEditingPromo({ ...editingPromo, level: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="writerName">Writer Name *</Label>
                  <Input
                    id="writerName"
                    placeholder="Enter writer name"
                    value={editingPromo.writerName}
                    onChange={(e) => setEditingPromo({ ...editingPromo, writerName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="producerName">Producer Name</Label>
                  <Input
                    id="producerName"
                    placeholder="Enter producer name"
                    value={editingPromo.producerName}
                    onChange={(e) => setEditingPromo({ ...editingPromo, producerName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bestCpi">Best CPI</Label>
                  <Input
                    id="bestCpi"
                    type="number"
                    placeholder="0"
                    value={editingPromo.bestCpi}
                    onChange={(e) => setEditingPromo({ ...editingPromo, bestCpi: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="percentMaleInCpi">% Male in CPI</Label>
                  <Input
                    id="percentMaleInCpi"
                    type="number"
                    placeholder="0"
                    value={editingPromo.percentMaleInCpi}
                    onChange={(e) => setEditingPromo({ ...editingPromo, percentMaleInCpi: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bestCpt">Best CPT</Label>
                  <Input
                    id="bestCpt"
                    type="number"
                    placeholder="0"
                    value={editingPromo.bestCpt}
                    onChange={(e) => setEditingPromo({ ...editingPromo, bestCpt: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="percentMaleInCpt">% Male in CPT</Label>
                  <Input
                    id="percentMaleInCpt"
                    type="number"
                    placeholder="0"
                    value={editingPromo.percentMaleInCpt}
                    onChange={(e) => setEditingPromo({ ...editingPromo, percentMaleInCpt: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* Array Fields */}
              <div className="space-y-4">
                {/* Promos Tested */}
                <div className="space-y-2">
                  <Label>Promos Tested</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add promo name"
                      value={tempInputs.promosTested}
                      onChange={(e) => setTempInputs({ ...tempInputs, promosTested: e.target.value })}
                      onKeyDown={(e) => handleKeyPress('promosTested', e)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addArrayItem('promosTested', tempInputs.promosTested)}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editingPromo.promosTested?.map((promo, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {promo}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => removeArrayItem('promosTested', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Promos with CPI < 2 */}
                <div className="space-y-2">
                  <Label>Promos with CPI &lt; 2</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add promo name"
                      value={tempInputs.promosWithCpiLessThan2}
                      onChange={(e) => setTempInputs({ ...tempInputs, promosWithCpiLessThan2: e.target.value })}
                      onKeyDown={(e) => handleKeyPress('promosWithCpiLessThan2', e)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addArrayItem('promosWithCpiLessThan2', tempInputs.promosWithCpiLessThan2)}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editingPromo.promosWithCpiLessThan2?.map((promo, index) => (
                      <Badge key={index} variant="default" className="flex items-center gap-1">
                        {promo}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => removeArrayItem('promosWithCpiLessThan2', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Promos with CPT < 50 */}
                <div className="space-y-2">
                  <Label>Promos with CPT &lt; 50</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add promo name"
                      value={tempInputs.promosWithCptLessThan50}
                      onChange={(e) => setTempInputs({ ...tempInputs, promosWithCptLessThan50: e.target.value })}
                      onKeyDown={(e) => handleKeyPress('promosWithCptLessThan50', e)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addArrayItem('promosWithCptLessThan50', tempInputs.promosWithCptLessThan50)}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editingPromo.promosWithCptLessThan50?.map((promo, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        {promo}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => removeArrayItem('promosWithCptLessThan50', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={savePromo}
                  disabled={!editingPromo.level || !editingPromo.writerName}
                >
                  Save Promo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};